import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, RefreshControl, ScrollView, Text, TouchableOpacity, View } from 'react-native';
import { useNavigation, useRouter } from 'expo-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { StyleSheet } from 'react-native-unistyles';
import SearchBox from '@/components/General/inputs/SearchBox';
import SelectInput from '@/components/General/inputs/SelectInput';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import SheetModal from '@/components/General/SheetModal';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import FormCard from '@/components/Forms/FormCard';
import Unicon from '@/components/General/Unicon';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { archiveForm, deleteForm, deleteFormFolder, moveFormToFolder, unarchiveForm } from '@/data/forms';
import { showAlert } from '@/utils/showAlert';
import { buildFolderTree, descendantFolderIDs, flattenFolderTree, folderPath } from '@/utils/formFolders';
import { canDeleteForm, canEditForm, canManageFormEntries } from '@/utils/formPermissions';

const FilterChip = ({ label, selected, onPress, testID }) => (
    <TouchableOpacity testID={testID} accessibilityRole="button" accessibilityState={{ selected }} accessibilityLabel={`Filter by ${label}`} onPress={onPress} style={styles.filterChip(selected)}>
        <Text style={styles.filterChipText(selected)}>{label}</Text>
    </TouchableOpacity>
);

export default function FormsLibraryScreen() {
    const router = useRouter();
    const navigation = useNavigation();
    const queryClient = useQueryClient();
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState('all');
    const [visibility, setVisibility] = useState('all');
    const [folderFilter, setFolderFilter] = useState(null);
    const [moveForm, setMoveForm] = useState(null);
    const { data: user } = Query.people.loggedInUser();
    const { data: forms = [], isLoading: formsLoading, isFetching: formsFetching } = Query.forms.all();
    const { data: folders = [], isLoading: foldersLoading, isFetching: foldersFetching } = Query.formFolders.all();
    const canManageFolders = user?.isAdmin || user?.can?.('Create Forms');

    useEffect(() => {
        navigation.setOptions({
            headerRight: () => canManageFolders ? (
                <DropdownMenu testID="forms-library-create-menu" options={[
                    { name: 'Create Form', icon: dropdownIcons.create, onSelect: () => router.push('/forms/create') },
                    { name: 'Create Folder', icon: dropdownIcons.createFolder, onSelect: () => router.push('/forms/folders/create') },
                ]} />
            ) : null,
        });
    }, [canManageFolders, navigation, router]);

    const folderTree = useMemo(() => buildFolderTree(folders), [folders]);
    const flatFolders = useMemo(() => flattenFolderTree(folderTree), [folderTree]);
    const folderChoices = useMemo(() => flatFolders.map((folder) => ({ ...folder, name: folderPath(folders, folder.id) })), [flatFolders, folders]);
    const folderQuery = { data: folderChoices, isLoading: false, isFetching: false, refetch: () => {} };

    const filtered = useMemo(() => {
        const allowedFolders = folderFilter ? descendantFolderIDs(folders, folderFilter) : null;
        const term = search.trim().toLowerCase();
        return forms.filter((form) => {
            if (term && !`${form.title || ''} ${form.description || ''}`.toLowerCase().includes(term)) return false;
            if (status !== 'all' && form.status !== status) return false;
            if (visibility === 'public' && !form.isPublic) return false;
            if (visibility === 'restricted' && form.isPublic) return false;
            if (allowedFolders && !allowedFolders.has(form.folderID)) return false;
            return true;
        });
    }, [folderFilter, folders, forms, search, status, visibility]);

    const invalidate = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.forms.all() });
        queryClient.invalidateQueries({ queryKey: QueryKeys.formFolders.all() });
    }, [queryClient]);
    const deleteMutation = useMutation({ mutationFn: deleteForm, onSuccess: invalidate });
    const archiveMutation = useMutation({ mutationFn: ({ formID, unarchive }) => (unarchive ? unarchiveForm(formID) : archiveForm(formID)), onSuccess: invalidate });
    const deleteFolderMutation = useMutation({ mutationFn: deleteFormFolder, onSuccess: invalidate });
    const moveMutation = useMutation({ mutationFn: moveFormToFolder, onSuccess: () => { invalidate(); setMoveForm(null); } });

    const folderMenu = useCallback((folder) => [
        { name: 'New Subfolder', icon: dropdownIcons.add, onSelect: () => router.push({ pathname: '/forms/folders/create', params: { parentFolderID: folder.id } }) },
        { name: 'Rename Folder', icon: dropdownIcons.edit, onSelect: () => router.push({ pathname: '/forms/folders/create', params: { id: folder.id } }) },
        { name: 'Delete Folder', icon: dropdownIcons.delete, destructive: true, onSelect: () => showAlert({
            title: 'Delete Folder',
            subtitle: `Delete "${folder.name}"? Contained forms become uncategorized and all subfolders are deleted. Form permissions will not change.`,
            confirmText: 'Delete',
            isDestructive: true,
            onConfirm: () => deleteFolderMutation.mutate({ folderID: folder.id }),
        }) },
    ], [deleteFolderMutation, router]);

    const formMenu = useCallback((form) => {
        const options = [];
        if (canManageFormEntries(form, user)) options.push({ name: 'View Entries', icon: dropdownIcons.unhide, onSelect: () => router.push(`/forms/${form.id}?tab=entries`) });
        if (canEditForm(form, user)) {
            options.push({ name: 'Edit Form', icon: dropdownIcons.edit, onSelect: () => router.push(`/forms/${form.id}`) });
            options.push({ name: 'Move to Folder', icon: dropdownIcons.create, onSelect: () => setMoveForm(form) });
            options.push(form.status === 'archived'
                ? { name: 'Unarchive Form', icon: dropdownIcons.unarchive, onSelect: () => archiveMutation.mutate({ formID: form.id, unarchive: true }) }
                : { name: 'Archive Form', icon: dropdownIcons.archive, onSelect: () => showAlert({
                    title: 'Archive Form',
                    subtitle: `Archive "${form.title}"? It will be hidden from members and stop accepting responses. You can unarchive it later — it will return as a draft.`,
                    confirmText: 'Archive',
                    isDestructive: true,
                    onConfirm: () => archiveMutation.mutate({ formID: form.id }),
                }) });
        }
        if (canDeleteForm(form, user)) options.push({
            name: 'Delete Form', icon: dropdownIcons.delete, destructive: true, onSelect: () => showAlert({
                title: 'Delete Form', subtitle: `Delete "${form.title}" and all of its entries? This cannot be undone.`, confirmText: 'Delete', isDestructive: true,
                onConfirm: () => deleteMutation.mutate({ formID: form.id }),
            }),
        });
        return options;
    }, [archiveMutation, deleteMutation, router, user]);

    const renderForms = (items) => <View style={styles.cards}>{items.map((form) => (
        <FormCard key={form.id} form={form} onPress={() => router.push(`/forms/${form.id}`)} menuOptions={formMenu(form)} showEntryCount={canManageFormEntries(form, user)} />
    ))}</View>;

    const loading = formsLoading || foldersLoading;
    const uncategorized = filtered.filter((form) => !form.folderID);

    return (
        <ScrollView testID="forms-list" style={styles.root} contentContainerStyle={styles.content} refreshControl={<RefreshControl refreshing={!loading && (formsFetching || foldersFetching)} onRefresh={invalidate} />}>
            <View style={styles.intro}>
                <View><Text style={styles.eyebrow}>FORM LIBRARY</Text><Text style={styles.title}>All forms</Text><Text style={styles.subtitle}>Folders organize forms only. Access remains controlled by form visibility and roles.</Text></View>
            </View>
            <SearchBox testID="forms-library-search" searchText={search} onChangeText={setSearch} placeholder="Search forms..." />
            <View style={styles.filters}>
                <View style={styles.filterGroup}>{['all', 'draft', 'published', 'closed', 'archived'].map((value) => <FilterChip key={value} label={value === 'all' ? 'All statuses' : value} selected={status === value} onPress={() => setStatus(value)} testID={`forms-status-filter-${value}`} />)}</View>
                <View style={styles.filterGroup}>{['all', 'public', 'restricted'].map((value) => <FilterChip key={value} label={value === 'all' ? 'All visibility' : value} selected={visibility === value} onPress={() => setVisibility(value)} testID={`forms-visibility-filter-${value}`} />)}</View>
            </View>
            <SelectInput title="Folder" placeholder="All folders" value={folderFilter} onUpdate={setFolderFilter} options={[{ label: 'All folders', value: null }, ...folderChoices.map((folder) => ({ label: folder.name, value: folder.id }))]} />

            {loading ? <View style={styles.loading}><ActivityIndicator size="large" /></View> : filtered.length === 0 ? (
                <EmptyListPlaceholder spacing={10} message={forms.length === 0 ? (canManageFolders ? 'No forms yet. Create a form to start collecting responses.' : 'No forms are available to you.') : 'No forms match these filters.'} />
            ) : folderFilter ? renderForms(filtered) : (
                <View style={styles.groups}>
                    {flatFolders.map((folder) => {
                        const items = filtered.filter((form) => form.folderID === folder.id);
                        if (items.length === 0 && !canManageFolders) return null;
                        return <View key={folder.id} style={styles.folderGroup}>
                            <View style={styles.folderHeader(folder.depth)}>
                                <Unicon name="folder-outline" size={17} colorVariant="forms" />
                                <Text style={styles.folderName}>{folder.name}</Text>
                                <Text style={styles.folderCount}>{items.length}</Text>
                                {canManageFolders && <DropdownMenu testID={`form-folder-menu-${folder.id}`} options={folderMenu(folder)} />}
                            </View>
                            {items.length > 0 && renderForms(items)}
                        </View>;
                    })}
                    {uncategorized.length > 0 && <View style={styles.folderGroup}><View style={styles.folderHeader(0)}><Unicon name="documents-outline" size={17} colorVariant="textSecondary" /><Text style={styles.folderName}>Uncategorized</Text><Text style={styles.folderCount}>{uncategorized.length}</Text></View>{renderForms(uncategorized)}</View>}
                </View>
            )}

            <SheetModal isOpen={!!moveForm} title={moveForm ? `Move ${moveForm.title}` : 'Move Form'} cancelText="Cancel" onCancel={() => setMoveForm(null)} onClose={() => setMoveForm(null)}>
                <SheetSelector title="Destination Folder" placeholder="Uncategorized" type="roleFolders" queryResult={folderQuery} value={folderChoices.find((folder) => folder.id === moveForm?.folderID) || null} onChange={(folder) => moveMutation.mutate({ formID: moveForm.id, folderID: folder?.id || null })} />
                {!!moveForm?.folderID && <TouchableOpacity testID="form-move-unfiled" accessibilityRole="button" accessibilityLabel="Move form to uncategorized" style={styles.unfile} onPress={() => moveMutation.mutate({ formID: moveForm.id, folderID: null })}><Text style={styles.unfileText}>Move to uncategorized</Text></TouchableOpacity>}
            </SheetModal>
        </ScrollView>
    );
}

const styles = StyleSheet.create((theme) => ({
    root: { flex: 1, backgroundColor: 'transparent' },
    content: { width: '100%', maxWidth: 900, alignSelf: 'center', padding: 16, paddingBottom: 100, gap: 14 },
    intro: { flexDirection: 'row', justifyContent: 'space-between', gap: 12 },
    eyebrow: { color: theme.colors.forms, fontFamily: theme.fonts.bold, fontSize: 10, letterSpacing: 1.1 },
    title: { color: theme.colors.text, fontFamily: theme.fonts.heading, fontSize: 24, marginTop: 3 },
    subtitle: { color: theme.colors.textSecondary, fontSize: 13, lineHeight: 18, marginTop: 4 },
    filters: { gap: 8 },
    filterGroup: { flexDirection: 'row', flexWrap: 'wrap', gap: 7 },
    filterChip: (selected) => ({ paddingHorizontal: 10, paddingVertical: 6, borderRadius: 8, backgroundColor: selected ? theme.colors.formsContainer : theme.colors.surface }),
    filterChipText: (selected) => ({ color: selected ? theme.colors.forms : theme.colors.textSecondary, fontFamily: theme.fonts.bold, fontSize: 11, textTransform: 'capitalize' }),
    loading: { padding: 50, alignItems: 'center' },
    groups: { gap: 18 },
    folderGroup: { gap: 9 },
    folderHeader: (depth) => ({ flexDirection: 'row', alignItems: 'center', gap: 8, marginLeft: depth * 18, minHeight: 32 }),
    folderName: { color: theme.colors.text, fontFamily: theme.fonts.heading, fontSize: 14, flex: 1 },
    folderCount: { color: theme.colors.textSecondary, fontSize: 11 },
    cards: { gap: 9 },
    unfile: { marginTop: 12, padding: 12, alignItems: 'center' },
    unfileText: { color: theme.colors.forms, fontFamily: theme.fonts.bold, fontSize: 13 },
}));
